import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CONTACT, submitEnquiry } from "../lib/site";

const BUSINESS_TYPES = ["Kebab Shop", "Fish & Chips Shop", "Takeaway", "Café", "Restaurant", "Other"];

const initialForm = { name: "", business: "", business_type: "Kebab Shop", phone: "", email: "", message: "" };

const INFO = [
  { icon: MapPin, label: "Address", value: CONTACT.address, href: null, testid: "contact-address" },
  { icon: Phone, label: "Phone", value: CONTACT.phone, href: CONTACT.phoneHref, testid: "contact-phone" },
  { icon: Mail, label: "Email", value: CONTACT.email, href: CONTACT.emailHref, testid: "contact-email" },
  { icon: Clock, label: "Support hours", value: CONTACT.hours, href: null, testid: "contact-hours" },
];

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitEnquiry(form);
      toast.success("Thanks — your enquiry has been sent. We'll be in touch within one working day.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Could not send your enquiry right now. Please email us directly at " + CONTACT.email);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 lg:py-36 bg-[#0b0c10] overflow-hidden">
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-amber-500/10 blur-[150px]" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          className="max-w-2xl mb-16"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="eyebrow mb-5">Contact</p>
          <h2 data-testid="contact-headline" className="font-display font-semibold text-white tracking-tight leading-[1.05] text-3xl sm:text-5xl">
            Ready to transform <span className="italic text-amber-500">your counter?</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-gray-400 leading-relaxed">
            Tell us about your shop and we'll set up a personalised demo of Your Online Store — usually
            within 24 hours.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <motion.div
            className="lg:col-span-5 space-y-4"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
          >
            {INFO.map((item) => {
              const Icon = item.icon;
              const inner = (
                <>
                  <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-amber-500/10 border border-amber-500/25 text-amber-400 shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-mono-x text-[10px] tracking-[0.25em] uppercase text-gray-500">{item.label}</div>
                    <div className="mt-1 text-white text-sm sm:text-base">{item.value}</div>
                  </div>
                </>
              );
              return item.href ? (
                <a
                  key={item.label}
                  data-testid={item.testid}
                  href={item.href}
                  className="flex items-center gap-4 rounded-2xl bg-[#171923] border border-amber-500/15 hover:border-amber-500/50 p-5 transition-colors duration-300"
                >
                  {inner}
                </a>
              ) : (
                <div key={item.label} data-testid={item.testid} className="flex items-center gap-4 rounded-2xl bg-[#171923] border border-amber-500/15 p-5">
                  {inner}
                </div>
              );
            })}
            <div className="clip-frame border border-amber-500/20 mt-6 hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1617347454431-f49d7ff5c3b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxmb29kJTIwZGVsaXZlcnklMjBjb3VyaWVyJTIwZHJpdmVyJTIwc2Nvb3RlciUyMHBhY2thZ2V8ZW58MHx8fHwxNzkzNzk2NzM4fDA&ixlib=rb-4.1.0&q=85&auto=format&fit=crop&w=1200"
                alt="Delivery rider on the road with an order"
                className="w-full h-48 object-cover"
              />
            </div>
          </motion.div>

          <motion.form
            data-testid="contact-form"
            onSubmit={onSubmit}
            className="lg:col-span-7 rounded-2xl bg-[#12141c]/85 backdrop-blur-xl border border-amber-500/20 p-6 sm:p-10 shadow-[0_8px_40px_rgba(0,0,0,0.45)]"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="cf-name" className="block font-mono-x text-[10px] tracking-[0.25em] uppercase text-gray-500 mb-2">Your name *</label>
                <input id="cf-name" data-testid="contact-input-name" required value={form.name} onChange={set("name")} className="input-dark" placeholder="e.g. Ahmet Yilmaz" />
              </div>
              <div>
                <label htmlFor="cf-business" className="block font-mono-x text-[10px] tracking-[0.25em] uppercase text-gray-500 mb-2">Business name</label>
                <input id="cf-business" data-testid="contact-input-business" value={form.business} onChange={set("business")} className="input-dark" placeholder="e.g. Sultan Kebab & Grill" />
              </div>
              <div>
                <label htmlFor="cf-type" className="block font-mono-x text-[10px] tracking-[0.25em] uppercase text-gray-500 mb-2">Business type</label>
                <select id="cf-type" data-testid="contact-select-type" value={form.business_type} onChange={set("business_type")} className="input-dark">
                  {BUSINESS_TYPES.map((t) => (
                    <option key={t} value={t} className="bg-[#12141c]">{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="cf-phone" className="block font-mono-x text-[10px] tracking-[0.25em] uppercase text-gray-500 mb-2">Phone</label>
                <input id="cf-phone" data-testid="contact-input-phone" type="tel" value={form.phone} onChange={set("phone")} className="input-dark" placeholder="0450 123 456" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cf-email" className="block font-mono-x text-[10px] tracking-[0.25em] uppercase text-gray-500 mb-2">Email address *</label>
                <input id="cf-email" data-testid="contact-input-email" type="email" required value={form.email} onChange={set("email")} className="input-dark" placeholder="you@yourshop.com.au" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cf-message" className="block font-mono-x text-[10px] tracking-[0.25em] uppercase text-gray-500 mb-2">Tell us about your business *</label>
                <textarea id="cf-message" data-testid="contact-input-message" required rows={5} value={form.message} onChange={set("message")} className="input-dark resize-none" placeholder="How many orders do you handle daily? Do you need driver tracking?" />
              </div>
            </div>
            <button
              data-testid="contact-form-submit-button"
              type="submit"
              disabled={sending}
              className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-[#0b0c10] font-bold px-10 py-4 rounded-full shadow-[0_0_30px_rgba(245,158,11,0.4)] transition-colors duration-200"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Sending…" : "Send Enquiry"}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
